import { Graphics } from 'pixi.js';
import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { VfxEffect, LingerZone } from '../labyrinth/Projectile';
import type { SpriteBaker, BakedSprite } from './SpriteBaker';

const VFX_TEX_PX   = 192;
const VFX_Y_OFFSET = 0.4;
const VFX_RENDER_ORDER = 15;
const LINGER_RENDER_ORDER = 4;
const LINGER_Y_OFFSET = 0.3;

interface Entry {
  sprite: BakedSprite;
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
}

/**
 * Ground-plane billboards for ephemeral VFX (muzzle_flash / explosion / beam
 * / melee_slash / death_explosion / robot_hit) and persistent linger zones.
 * Each effect re-bakes a PIXI Graphics render of its 2D-path drawing into a
 * CanvasTexture each frame, so the visuals match [LabyrinthState.drawProjectiles].
 */
export class Vfx3D {
  readonly group = new THREE.Group();
  private readonly geo: THREE.PlaneGeometry;
  private readonly vfxEntries = new Map<VfxEffect, Entry>();
  private readonly lingerEntries = new Map<LingerZone, Entry>();

  constructor(private readonly baker: SpriteBaker) {
    this.geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  }

  sync(vfx: ReadonlyArray<VfxEffect>, lingers: ReadonlyArray<LingerZone>): void {
    // ── ephemeral VFX ───────────────────────────────────────
    const aliveVfx = new Set<VfxEffect>();
    for (const v of vfx) {
      aliveVfx.add(v);
      let entry = this.vfxEntries.get(v);
      if (!entry) {
        entry = this.createEntry(VFX_RENDER_ORDER);
        this.vfxEntries.set(v, entry);
      }
      this.updateVfx(entry, v);
    }
    for (const [v, entry] of this.vfxEntries) {
      if (aliveVfx.has(v)) continue;
      this.disposeEntry(entry);
      this.vfxEntries.delete(v);
    }

    // ── linger zones ───────────────────────────────────────
    const aliveLinger = new Set<LingerZone>();
    for (const z of lingers) {
      aliveLinger.add(z);
      let entry = this.lingerEntries.get(z);
      if (!entry) {
        entry = this.createEntry(LINGER_RENDER_ORDER);
        this.lingerEntries.set(z, entry);
      }
      this.updateLinger(entry, z);
    }
    for (const [z, entry] of this.lingerEntries) {
      if (aliveLinger.has(z)) continue;
      this.disposeEntry(entry);
      this.lingerEntries.delete(z);
    }
  }

  private createEntry(renderOrder: number): Entry {
    const sprite = this.baker.createSprite(VFX_TEX_PX);
    const material = new THREE.MeshBasicMaterial({
      map: sprite.texture,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(this.geo, material);
    mesh.renderOrder = renderOrder;
    this.group.add(mesh);
    return { sprite, mesh, material };
  }

  private disposeEntry(entry: Entry): void {
    this.group.remove(entry.mesh);
    entry.material.dispose();
    this.baker.disposeSprite(entry.sprite);
  }

  // ── VFX dispatch ────────────────────────────────────────

  private updateVfx(entry: Entry, v: VfxEffect): void {
    const progress = Math.max(0, Math.min(1, 1 - v.life / v.maxLife));

    // Bounding extent of this effect in tile units (half the quad side).
    let cx = v.x;
    let cy = v.y;
    let half: number;

    switch (v.kind) {
      case 'muzzle_flash':
        half = Math.max(v.radius, 0.5);
        break;
      case 'explosion':
        half = Math.max(v.radius * 1.5, 0.6);
        break;
      case 'beam': {
        const tx = v.tx ?? v.x;
        const ty = v.ty ?? v.y;
        cx = (v.x + tx) / 2;
        cy = (v.y + ty) / 2;
        half = Math.max(Math.abs(tx - v.x), Math.abs(ty - v.y)) / 2 + 0.5;
        break;
      }
      case 'melee_slash':
        half = Math.max(v.radius * 1.2, 0.8);
        break;
      case 'death_explosion':
        half = Math.max(v.radius * 1.8, 1.5);
        break;
      case 'robot_hit':
        half = Math.max(v.radius * 1.5, 0.8);
        break;
      default:
        half = 0.5;
    }

    const halfPx = half * TILE_SIZE;
    const lx = (v.x - cx) * TILE_SIZE;
    const ly = (v.y - cy) * TILE_SIZE;

    this.baker.bake(entry.sprite, halfPx, (scratch) => {
      const g = new Graphics();
      scratch.addChild(g);
      drawVfxLocal(g, v, progress, lx, ly, cx, cy);
    });

    entry.mesh.position.set(cx * TILE_SIZE, VFX_Y_OFFSET, cy * TILE_SIZE);
    entry.mesh.scale.set(halfPx * 2, 1, halfPx * 2);
  }

  private updateLinger(entry: Entry, z: LingerZone): void {
    const half = Math.max(z.radius * 1.2, 0.4);
    const halfPx = half * TILE_SIZE;

    this.baker.bake(entry.sprite, halfPx, (scratch) => {
      const g = new Graphics();
      scratch.addChild(g);
      drawLingerLocal(g, z);
    });

    entry.mesh.position.set(z.x * TILE_SIZE, LINGER_Y_OFFSET, z.y * TILE_SIZE);
    entry.mesh.scale.set(halfPx * 2, 1, halfPx * 2);
  }

  dispose(): void {
    for (const entry of this.vfxEntries.values()) this.disposeEntry(entry);
    this.vfxEntries.clear();
    for (const entry of this.lingerEntries.values()) this.disposeEntry(entry);
    this.lingerEntries.clear();
    this.geo.dispose();
  }
}

// ── Local-space 2D draw ports ──────────────────────────────────
// These mirror the per-kind cases in LabyrinthState.drawProjectiles, but
// positioned around local origin (px=ly, py=lx after tile→pixel convert)
// because the bake frame is centered on the quad.

function drawVfxLocal(
  g: Graphics, v: VfxEffect, progress: number,
  lx: number, ly: number, cx: number, cy: number,
): void {
  const px = lx;
  const py = ly;

  switch (v.kind) {
    case 'muzzle_flash': {
      const r = (1 - progress) * TILE_SIZE * v.radius;
      g.circle(px, py, r);
      g.fill({ color: v.color, alpha: 1 - progress });
      if (progress < 0.3) {
        g.circle(px, py, r * 0.4);
        g.fill({ color: 0xffffff, alpha: (0.3 - progress) * 2 });
      }
      break;
    }
    case 'explosion': {
      const r = progress * TILE_SIZE * v.radius;
      const alpha = 1 - progress;
      g.circle(px, py, r);
      g.fill({ color: v.color, alpha: alpha * 0.4 });
      g.circle(px, py, r);
      g.stroke({ color: 0xffcc00, width: 2 + v.radius * 0.5, alpha: alpha * 0.8 });
      if (progress < 0.3) {
        const innerR = r * 0.5;
        g.circle(px, py, innerR);
        g.fill({ color: 0xffffff, alpha: (0.3 - progress) * 3 });
      }
      if (progress > 0.1 && progress < 0.6) {
        const shockR = r * 1.4;
        g.circle(px, py, shockR);
        g.stroke({ color: 0xffeedd, width: 1, alpha: (0.6 - progress) * 1.5 });
      }
      break;
    }
    case 'beam': {
      const alpha = 1 - progress;
      const tx = ((v.tx ?? v.x) - cx) * TILE_SIZE;
      const ty = ((v.ty ?? v.y) - cy) * TILE_SIZE;
      g.moveTo(px, py); g.lineTo(tx, ty);
      g.stroke({ color: v.color, width: 6, alpha: alpha * 0.2 });
      g.moveTo(px, py); g.lineTo(tx, ty);
      g.stroke({ color: v.color, width: 3, alpha: alpha * 0.5 });
      g.moveTo(px, py); g.lineTo(tx, ty);
      g.stroke({ color: 0xffffff, width: 1.5, alpha });
      if (progress < 0.4) {
        g.circle(tx, ty, 3 * (1 - progress));
        g.fill({ color: 0xffffff, alpha: (0.4 - progress) * 2 });
      }
      break;
    }
    case 'melee_slash': {
      const r = TILE_SIZE * v.radius;
      const alpha = 1 - progress;
      const startAngle = -Math.PI * 0.3 + progress * Math.PI * 0.6;
      const arcLen = Math.PI * 0.6;
      g.arc(px, py, r * (0.5 + progress * 0.5), startAngle, startAngle + arcLen);
      g.stroke({ color: v.color, width: 4, alpha: alpha * 0.4 });
      g.arc(px, py, r * (0.5 + progress * 0.5), startAngle, startAngle + arcLen);
      g.stroke({ color: 0xffffff, width: 2, alpha: alpha * 0.8 });
      break;
    }
    case 'death_explosion': {
      const r = TILE_SIZE * v.radius;
      if (progress < 0.15) {
        const flashP = progress / 0.15;
        const flashR = r * 0.6 * flashP;
        g.circle(px, py, flashR);
        g.fill({ color: 0xffffff, alpha: (1 - flashP) * 0.9 });
        g.circle(px, py, flashR * 0.5);
        g.fill({ color: v.color, alpha: 1 - flashP * 0.5 });
      }
      if (progress > 0.05 && progress < 0.6) {
        const fireP = (progress - 0.05) / 0.55;
        const fireR = r * (0.3 + fireP * 0.7);
        const fireAlpha = (1 - fireP) * 0.6;
        g.circle(px, py, fireR * 1.3);
        g.fill({ color: v.color, alpha: fireAlpha * 0.25 });
        g.circle(px, py, fireR);
        g.fill({ color: v.color, alpha: fireAlpha });
        g.circle(px, py, fireR * 0.4);
        g.fill({ color: 0xffdd44, alpha: fireAlpha * 1.2 });
        g.circle(px, py, fireR * 1.1);
        g.stroke({ color: 0xffcc00, width: 2, alpha: fireAlpha * 0.7 });
      }
      if (progress > 0.05) {
        const debrisP = (progress - 0.05) / 0.95;
        const seed = (v.x * 73.13 + v.y * 91.17) | 0;
        for (let i = 0; i < 8; i++) {
          const angle = ((seed + i * 1337) % 628) / 100;
          const speed = 0.6 + ((seed + i * 7) % 10) / 10 * 0.8;
          const dist = r * speed * debrisP;
          const dx = Math.cos(angle) * dist;
          const dy = Math.sin(angle) * dist;
          const size = (3 - debrisP * 2.5) * (1 + ((seed + i * 3) % 5) / 10);
          const debrisAlpha = Math.max(0, 1 - debrisP * 1.3);
          if (debrisAlpha > 0 && size > 0.3) {
            g.circle(px + dx, py + dy, size);
            g.fill({ color: i % 3 === 0 ? 0xffcc44 : v.color, alpha: debrisAlpha });
            const trailDx = dx * 0.7;
            const trailDy = dy * 0.7;
            g.circle(px + trailDx, py + trailDy, size * 0.5);
            g.fill({ color: v.color, alpha: debrisAlpha * 0.4 });
          }
        }
      }
      if (progress > 0.4) {
        const smokeP = (progress - 0.4) / 0.6;
        const smokeR = r * (0.8 + smokeP * 0.6);
        const smokeAlpha = (1 - smokeP) * 0.2;
        g.circle(px, py, smokeR);
        g.fill({ color: 0x444444, alpha: smokeAlpha });
      }
      if (progress > 0.05 && progress < 0.5) {
        const shockP = (progress - 0.05) / 0.45;
        const shockR = r * (0.5 + shockP * 1.2);
        g.circle(px, py, shockR);
        g.stroke({ color: 0xffeedd, width: 2 - shockP * 1.5, alpha: (1 - shockP) * 0.6 });
      }
      break;
    }
    case 'robot_hit': {
      const alpha = 1 - progress;
      const r = TILE_SIZE * v.radius;
      const flashR = r * (0.3 + progress * 0.7);
      g.circle(px, py, flashR);
      g.fill({ color: 0xff2222, alpha: alpha * 0.35 });
      if (progress < 0.4) {
        const coreAlpha = (0.4 - progress) * 2.5;
        g.circle(px, py, flashR * 0.3);
        g.fill({ color: 0xffaaaa, alpha: coreAlpha });
      }
      if (progress < 0.7) {
        const sparkAlpha = Math.max(0, 1 - progress * 1.5);
        const seed = (v.x * 53.7 + v.y * 71.3) | 0;
        for (let i = 0; i < 5; i++) {
          const angle = ((seed + i * 1259) % 628) / 100;
          const dist = flashR * (0.4 + progress * 1.2) * (0.6 + ((seed + i * 7) % 5) / 8);
          const sx = px + Math.cos(angle) * dist;
          const sy = py + Math.sin(angle) * dist;
          const size = (1.5 - progress * 1.5);
          if (size > 0.2) {
            g.circle(sx, sy, size);
            g.fill({ color: i % 2 === 0 ? 0xff4444 : 0xffaa44, alpha: sparkAlpha });
          }
        }
      }
      g.circle(px, py, flashR);
      g.stroke({ color: 0xff4444, width: 1.5, alpha: alpha * 0.5 });
      break;
    }
  }
}

function drawLingerLocal(g: Graphics, z: LingerZone): void {
  const zr = z.radius * TILE_SIZE;
  const alpha = Math.min(0.25, z.remaining * 0.15);
  g.circle(0, 0, zr);
  g.fill({ color: z.color, alpha });
  g.circle(0, 0, zr);
  g.stroke({ color: z.color, width: 1.5, alpha: alpha * 1.5 });
}

